import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share } from "lucide-react";
import { useApp } from "../context/AppContext";
import { LOGO_URL } from "../lib/brand";

const TX = {
  हिंदी: { title: "ऐप इंस्टॉल करें", desc: "होम स्क्रीन पर Agro Madat जोड़ें — तेज़, ऑफ़लाइन भी", btn: "इंस्टॉल करें", ios: "नीचे Share बटन दबाएँ, फिर ‘Add to Home Screen’ चुनें" },
  English: { title: "Install Agro Madat", desc: "Add it to your home screen — fast & works offline", btn: "Install", ios: "Tap the Share button, then choose ‘Add to Home Screen’" },
  Hinglish: { title: "App install karein", desc: "Home screen par Agro Madat jodein — fast aur offline", btn: "Install", ios: "Neeche Share button dabayein, phir ‘Add to Home Screen’ chunein" },
};

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function InstallPrompt() {
  const { language } = useApp();
  const x = TX[language] || TX["हिंदी"];
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem("am_pwa_dismissed") === "1") return;

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => { setShow(false); localStorage.setItem("am_pwa_dismissed", "1"); });

    // iOS Safari never fires beforeinstallprompt — show manual hint after a short delay
    let iosTimer;
    if (isIOS()) iosTimer = setTimeout(() => { setIos(true); setShow(true); }, 2500);

    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(iosTimer); };
  }, []);

  const dismiss = () => { setShow(false); localStorage.setItem("am_pwa_dismissed", "1"); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const res = await deferred.userChoice.catch(() => null);
    setDeferred(null); setShow(false);
    if (res?.outcome === "accepted") localStorage.setItem("am_pwa_dismissed", "1");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="pwa-install-banner"
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
          transition={{ type: "spring", stiffness: 260, damping: 26 }}
          className="fixed inset-x-3 bottom-[84px] lg:inset-x-auto lg:right-6 lg:bottom-6 lg:w-[380px] z-[95]
                     rounded-2xl bg-white/95 backdrop-blur-md border border-black/5 shadow-2xl p-3.5 flex items-center gap-3">
          <img src={LOGO_URL} alt="Agro Madat" className="h-11 w-11 rounded-xl object-contain bg-white shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-sm leading-tight text-[#1a1a1a]">{x.title}</p>
            <p className="text-[12px] text-muted-foreground leading-snug">{ios ? x.ios : x.desc}</p>
          </div>
          {!ios && (
            <button onClick={install} data-testid="pwa-install-btn"
              className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 tap active:scale-95 transition-transform">
              <Download className="h-4 w-4" />{x.btn}
            </button>
          )}
          {ios && <Share className="h-5 w-5 text-green-600 shrink-0" />}
          <button onClick={dismiss} data-testid="pwa-install-close" aria-label="Dismiss" className="shrink-0 text-muted-foreground hover:text-foreground p-1">
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
